import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline, useMediaQuery } from '@mui/material';
import { buildTheme } from '../theme';

// Color mode: 'system' (follow prefers-color-scheme), 'light', or 'dark'.
// The user's choice persists in localStorage; 'system' re-resolves live when
// the OS theme changes.

const STORAGE_KEY = 'hourtrax-color-mode';
const MODES = ['system', 'light', 'dark'];

const ColorModeContext = createContext({
  mode: 'system',
  resolvedMode: 'light',
  setMode: () => {},
  cycleMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export function ColorModeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return MODES.includes(saved) ? saved : 'system';
    } catch {
      return 'system';
    }
  });

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolvedMode = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const setMode = (next) => {
    if (!MODES.includes(next)) return;
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable (private mode) — mode still applies for the session
    }
  };

  // Keep the browser-native UI (scrollbars, form controls) in sync
  useEffect(() => {
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  const value = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode,
      cycleMode: () => setMode(MODES[(MODES.indexOf(mode) + 1) % MODES.length]),
    }),
    [mode, resolvedMode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default ColorModeContext;
